import React, { useEffect, useMemo, useState } from 'react';
import * as Icons from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { leadsApi } from '../api/endpoints';
import { buildWhatsappLink } from './LeadActivityDrawer';
import { toast } from 'sonner';

const absUrl = (url) => (url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export const SendBrochureDialog = ({ project, trigger }) => {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    leadsApi.list()
      .then(setLeads)
      .catch(() => toast.error('Failed to load leads'))
      .finally(() => setLoading(false));
  }, [open]);

  const sorted = useMemo(() => {
    const ql = q.toLowerCase();
    const matches = leads.filter(l =>
      !ql || `${l.name} ${l.phone} ${l.displayId || ''}`.toLowerCase().includes(ql)
    );
    const interested = matches.filter(l => l.property === project?.name);
    const others = matches.filter(l => l.property !== project?.name);
    return { interested, others };
  }, [leads, q, project]);

  const send = (lead) => {
    const link = absUrl(project.brochure_url);
    const msg = `Hello ${lead.name}, sharing the brochure for ${project.name}${project.location ? ` (${project.location})` : ''}: ${link}\n— Kiana Developers`;
    window.open(buildWhatsappLink(lead.phone, msg), '_blank', 'noopener');
    leadsApi.addActivity(lead.id, {
      type: 'whatsapp',
      text: `Brochure for ${project.name} shared via WhatsApp`,
      metadata: { brochure_url: project.brochure_url },
    }).catch(() => {});
    toast.success(`Opening WhatsApp for ${lead.name}`);
  };

  const LeadRow = ({ l }) => (
    <div className="flex items-center gap-3 py-2 px-2 rounded hover:bg-slate-50" data-testid={`brochure-lead-${l.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5">
          {l.displayId && <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded shrink-0">{l.displayId}</span>}
          <span className="truncate">{l.name}</span>
        </div>
        <div className="text-[11px] text-slate-500 truncate">{l.phone}{l.property ? ` · ${l.property}` : ''}</div>
      </div>
      <Button
        size="sm"
        className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
        onClick={() => send(l)}
        data-testid={`brochure-send-${l.id}`}
      >
        <Icons.MessageCircle className="w-3.5 h-3.5 mr-1" /> Send
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col" data-testid="send-brochure-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">WhatsApp Brochure — {project?.name}</DialogTitle>
          <DialogDescription>Pick a lead to send the brochure PDF in one tap.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search leads by name or phone..." value={q} onChange={e => setQ(e.target.value)} data-testid="brochure-lead-search" />
        </div>
        <div className="overflow-y-auto flex-1 -mx-2 px-2">
          {loading && <div className="text-sm text-slate-400 py-6 text-center">Loading leads…</div>}
          {!loading && sorted.interested.length > 0 && (
            <>
              <div className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider mt-2 mb-1 flex items-center gap-1.5">
                <Icons.Star className="w-3 h-3" /> Interested in this project
                <Badge variant="outline" className="text-[10px] ml-auto">{sorted.interested.length}</Badge>
              </div>
              {sorted.interested.map(l => <LeadRow key={l.id} l={l} />)}
            </>
          )}
          {!loading && sorted.others.length > 0 && (
            <>
              <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mt-3 mb-1">Other leads</div>
              {sorted.others.map(l => <LeadRow key={l.id} l={l} />)}
            </>
          )}
          {!loading && sorted.interested.length + sorted.others.length === 0 && (
            <div className="text-sm text-slate-400 py-6 text-center">No leads found.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SendBrochureDialog;
